import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-KE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export default function Journal() {
  const [articles, setArticles] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then((r) => setArticles(r.data))
      .catch(() => {});
  }, []);

  if (articles.length === 0) return null;

  return (
    <section
      id="journal"
      data-testid="journal-section"
      className="relative py-28 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <SectionLabel>Legal Updates</SectionLabel>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl leading-tight">
              Notes From <span className="text-gold">Chambers</span>
            </h2>
            <p className="text-ink-dim max-w-md text-sm sm:text-base leading-relaxed">
              Short, plain-language briefings on tax, litigation and commercial
              law in Kenya — written for clients, not textbooks.
            </p>
          </div>
        </FadeUp>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map((a, i) => (
            <motion.button
              key={a.id}
              data-testid={`journal-card-${i}`}
              onClick={() => navigate(`/journal/${a.id}`)}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6 }}
              className="group text-left rounded-3xl border border-gold/15 bg-navy-700/60 p-8 hover:border-gold/50 transition-colors duration-500 flex flex-col"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold border border-gold/25 rounded-full px-3 py-1">
                  {a.category}
                </span>
                <ArrowUpRight
                  size={18}
                  className="text-ink-muted group-hover:text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300"
                />
              </div>
              <h3 className="font-display text-lg mt-6 leading-snug group-hover:text-gold transition-colors duration-300">
                {a.title}
              </h3>
              <p className="mt-3 text-sm text-ink-dim leading-relaxed line-clamp-3">
                {a.summary}
              </p>
              <span className="font-mono text-xs text-ink-muted mt-auto pt-6">
                {fmtDate(a.created_at)}
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
